import {
  generateIdentifierForGa,
  generateLocationForEvent,
} from './utils/analyticUtils';
import { capitalizeFirstLetter } from './utils/stringUtils';

export class GaAnalyticEvents {
  static details = {
    detailsPageViewApi: (practice) => ({
      API: {
        tags: {
          practiceInformation: practice,
          eventName: 'DETAILS_PAGE_VIEWED',
        },
      },
    }),

    phoneNumberClick: {
      action: 'Click_Phone_Number',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },
  };

  static eComm = {
    card: {
      action: 'Click_Buy_Online',
      category: 'ecom_website_result_list',
    },

    page: {
      action: 'Click_Buy_Online',
      category: 'Locator_Results_Page',
    },

    findOnline: {
      action: 'Click_Find_Online',
      category: 'ecom_website_result_list',
    },

    logo: {
      action: 'Click_Logo',
      category: 'Locator_Results_E-Com',
    },

    eCommShown: {
      action: 'Ecomm_Listing_Shown',
      category: 'ShopNow_Result_List',
      nonInteraction: true,
    },

    ratingClick: {
      action: 'Star_Rating_Clicked',
      category: 'ShopNow_Result_List',
    },
  };

  static filters = {
    applyFilters: {
      action: 'Click_Filter_Done',
      category: 'Ecp_Results_List_Filter',
      nonInteraction: false,
    },

    applyFiltersClick: (appliedFilters, queryStrings) => {
      const gaFilterEvent = { event: 'locatorSearchFilters' };

      for (let index = 0; index < appliedFilters.length; ++index) {
        const filter = appliedFilters[index];
        gaFilterEvent[filter.key] = filter.values.join(',');
      }

      return {
        GA: {
          ...this.filters.applyFilters,
          label: queryStrings.replace(/&/g, ';').replace(/%20/g, ' '),
        },
        GTM: gaFilterEvent,
      };
    },

    toggleBookingOff: {
      action: 'Online_Booking_Toggle_Off',
      category: 'Ecp_Results_List',
      nonInteraction: false,
    },

    toggleBookingOn: {
      action: 'Online_Booking_Toggle_On',
      category: 'Ecp_Results_List',
      nonInteraction: false,
    },
  };

  static map = {
    book: {
      action: 'Click_Book_Now',
      category: 'Locator_Results_Map',
    },

    bookAppointmentClick: (practice) => {
      let buttonText = '';
      const gaIdentifier = generateIdentifierForGa(practice);

      if (practice.bookingForm) {
        buttonText =
          practice.bookingForm === 'sf-form'
            ? 'Request an appointment'
            : 'Book now';
      }

      return {
        GA: {
          ...this.map.book,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMapBookNow',
          locatorButtonText: buttonText,
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    bookAppointmentClickApi: (practice) => ({
      API: {
        tags: {
          practiceInformation: practice,
          eventName: 'BOOK_APPOINTMENT_CLICKED',
        },
      },
    }),

    call: {
      action: 'Click_Call_Now',
      category: 'Locator_Results_Map',
    },

    callNowClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.map.call,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMapCallButton',
          locatorButtonText: 'Call Now',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    callNowClickApi: (practice) => ({
      API: {
        tags: {
          practiceInformation: practice,
          eventName: 'PRACTICE_MAP_PHONE_NUMBER_CLICKED',
        },
      },
    }),

    closePin: {
      action: 'Click_Map_Pin_Close',
      category: 'Locator_Results_Map',
    },

    closePinClick: (address) => ({
      GA: {
        ...this.map.closePin,
        label: address,
      },
      GTM: {
        event: 'locatorMapClosePinClick',
        ...this.map.closePin,
        label: address,
      },
    }),

    fullscreen: {
      action: 'Click_Toggle_Fullscreen',
      category: 'Locator_Results_Map',
    },

    fullscreenClick: (address) => ({
      GA: {
        ...this.map.fullscreen,
        label: address,
      },
      GTM: {
        event: 'locatorMapClosePinClick',
        ...this.map.fullscreen,
        label: address,
      },
    }),

    mapPin: {
      action: 'Click_Map_Pin',
      category: 'Locator_Results_Map',
    },

    pinClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.map.mapPin,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMapPinClick',
          mapPinProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    practice: {
      action: 'Click_ECP_Information',
      category: 'Locator_Results_Map',
      nonInteraction: false,
    },

    practiceClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.map.practice,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMapPracticeClick',
          mapProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    practiceClickApi: (practice) => ({
      tags: {
        practiceInformation: practice,
        eventName: 'IN_MAP_CLICKED',
      },
    }),

    scrollMap: {
      action: 'Click_Drag_Map',
      category: 'Locator_Results_Map',
    },

    scrollMapChange: (address) => ({
      GA: {
        ...this.map.scrollMapChange,
        label: address,
      },
      GTM: {
        event: 'locatorMapScroll',
        ...this.map.scrollMapChange,
        label: address,
      },
    }),

    searchThisArea: {
      action: 'Click_Map_Search_Button',
      category: 'Locator_Results_Map',
    },

    searchThisAreaClick: (address) => ({
      GA: {
        ...this.map.searchThisArea,
        label: address,
      },
      GTM: {
        event: 'locatorMapSearchThisAreaButtonClick',
        ...this.map.searchThisArea,
        label: address,
      },
    }),
  };

  static practice = {
    detailsBackButton: {
      action: 'Click_Back_Button_Details_Page',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },

    detailsBackButtonClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.practice.detailsBackButton,
          label: gaIdentifier,
        },
        GTM: {
          event: 'BackButton',
          locatorButtonText: 'Go Back',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    detailCtaCall: {
      action: 'Click_Call_Now_Button',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },

    detailCtaBook: {
      action: 'Click_Book_Now',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },

    detailCtaOffer: {
      action: 'Click_View_Offer',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },

    directionsClick: {
      action: 'Click_Get_Directions',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },

    ecpShown: {
      action: 'ECP_Listing_Shown',
      category: 'Ecp_Results_List',
      nonInteraction: true,
    },

    moreDetails: {
      action: 'Click_Practice_Details',
      category: 'Ecp_Results_List',
    },

    resultsOffer: {
      action: 'Click_View_Offer',
      category: 'Ecp_Results_List',
      nonInteraction: false,
    },

    resultsCall: {
      action: 'Click_Call_Now_Button',
      category: 'Ecp_Results_List',
      nonInteraction: false,
    },

    resultsBook: {
      action: 'Click_Book_Now',
      category: 'Ecp_Results_List',
    },

    resultsPracticeClick: {},

    websiteClick: {
      action: 'Click_ECP_Website',
      category: 'Locator_Practice_Detail',
      nonInteraction: false,
    },
  };

  static practiceCard = {
    moreDetailsClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.practice.moreDetails,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMoreDetails',
          locatorButtonText: 'More Details',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    moreDetailsClickApi: (practice) => ({
      API: {
        tags: {
          practiceInformation: practice,
          eventName: 'IN_LIST_DETAILS_CLICKED',
        },
      },
    }),

    phoneNumberClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.practice.resultsCall,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorPhoneNumberClick',
          locatorButtonText: 'Call Now Phone Number',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    bookAppointmentClick: (practice, locatorUrl) => {
      let buttonText = '';
      const gaIdentifier = generateIdentifierForGa(practice);

      if (practice.bookingForm) {
        buttonText =
          practice.bookingForm === 'sf-form'
            ? 'Request an appointment'
            : 'Book now';
      }

      return {
        GA: {
          ...this.practice.resultsBook,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorMainApptCTA',
          locatorButtonText: buttonText,
          locatorLinkUrl: locatorUrl,
          apptProvider: gaIdentifier,
        },
      };
    },
  };

  static practiceInfo = {
    directionsClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.practice.directionsClick,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorDetailGetDirections',
          locatorButtonText: 'Get Directions',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    directionsClickApi: (practice) => ({
      API: {
        tags: {
          practiceInformation: practice,
          eventName: 'GET_DIRECTIONS_CLICKED',
        },
      },
    }),

    phoneNumberClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.details.phoneNumberClick,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorPhoneNumberClick',
          locatorButtonText: 'Call Now Phone Number',
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    websiteClick: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);

      return {
        GA: {
          ...this.practice.websiteClick,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorDetailECPLink',
          locatorButtonText: 'ECP Link',
          locatorLinkURL: practice.website,
          apptProvider: gaIdentifier,
          apptLocation: generateLocationForEvent(practice),
        },
      };
    },

    websiteClickApi: (practice) => ({
      API: {
        tags: {
          eventName: 'WEBSITE_CLICKED',
          practiceInformation: practice,
        },
      },
    }),

    bookAppointmentClick: (practice) => {
      let buttonText = '';
      const gaIdentifier = generateIdentifierForGa(practice);

      if (practice.bookingForm) {
        buttonText =
          practice.bookingForm === 'sf-form'
            ? 'Request an appointment'
            : 'Book now';
      }

      return {
        GA: {
          ...this.practice.websiteClick,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorDetailMainApptCTA',
          locatorButtonText: buttonText,
          locatorLinkUrl: window.location.href,
          apptProvider: gaIdentifier,
        },
      };
    },

    bookAppointmentSuccess: (practice) => {
      const gaIdentifier = generateIdentifierForGa(practice);
      let booking_type = '';

      if (practice.bookingForm) {
        if (practice.bookingForm === 'sf-form') {
          booking_type = 'Appointment request';
        } else {
          booking_type = 'Online booking';
        }
      }
      return {
        GA: {
          ...this.practice.websiteClick,
          label: gaIdentifier,
        },
        GTM: {
          event: 'locatorDetailAppointmentSuccess',
          locatorAppointmentType: booking_type,
          locatorLinkUrl: window.location.href,
          apptProvider: gaIdentifier,
        },
      };
    },

    reviewSourceTabClick: (practice, tabIndex, reviewData) => {
      const gaLocationLabel = generateLocationForEvent(practice);
      const gaIdentifierLabel = generateIdentifierForGa(practice);
      const reviewDataKeys = Object.keys(reviewData);
      const reviewSource = reviewDataKeys[tabIndex];

      return {
        GA: {
          category: 'Reviews',
          action: `Click_${capitalizeFirstLetter(reviewSource)}_Tab`,
          nonInteraction: false,
          label: `${gaIdentifierLabel}Rating=${reviewData[reviewSource].starRating};Source=${reviewSource}`,
        },
        GTM: {
          event: 'locatorTabInReview',
          locatorButtonText: reviewSource,
          apptProvider: gaIdentifierLabel,
          apptLocation: gaLocationLabel,
        },
      };
    },

    reviewPolicyClick: (practice, reviewSource, reviewData) => {
      const gaLocationLabel = generateLocationForEvent(practice);
      const gaIdentifierLabel = generateIdentifierForGa(practice);

      return {
        GA: {
          category: 'Reviews',
          action: 'Click_Review_Policy',
          nonInteraction: true,
          label: `${gaIdentifierLabel}Rating=${reviewData.starRating};Source=${reviewSource}`,
        },
        GTM: {
          event: 'locatorReviewPolicyClick',
          apptProvider: gaIdentifierLabel,
          apptLocation: gaLocationLabel,
        },
      };
    },

    reviewTooltipClick: (practice, reviewSource, reviewData) => {
      const gaLocationLabel = generateLocationForEvent(practice);
      const gaIdentifierLabel = generateIdentifierForGa(practice);

      return {
        GA: {
          category: 'Reviews',
          action: 'Click_Review_Summary_Tooltip',
          nonInteraction: true,
          label: `${gaIdentifierLabel}Rating=${reviewData.starRating};Source=${reviewSource}`,
        },
        GTM: {
          event: 'locatorQuestionMarkInReview',
          locatorButtonText: 'question mark in review',
          apptProvider: gaIdentifierLabel,
          apptLocation: gaLocationLabel,
        },
      };
    },

    reviewSeeAllReviewsClick: (practice, starRating, reviewSource) => {
      const gaLocationLabel = generateLocationForEvent(practice);
      const gaIdentifierLabel = generateIdentifierForGa(practice);

      return {
        GA: {
          category: 'Reviews',
          action: 'Click_See_All_Reviews',
          nonInteraction: false,
          label: `${gaIdentifierLabel};Rating=${starRating};Source=${reviewSource}`,
        },
        GTM: {
          event: 'locatorSeeAllReview',
          locatorButtonText: 'See all reviews',
          apptProvider: gaIdentifierLabel,
          apptLocation: gaLocationLabel,
        },
      };
    },
  };

  static appointmentBookingInfo = {
    sortReviews: (practiceIdentifier) => {
      const GA_OBJECT = {
        action: 'Click_Sort_Review_Dropdown',
        category: 'Locator_Doctor_Bio_Page',
        label: practiceIdentifier,
      };

      return {
        GA: GA_OBJECT,
        GTM: {
          event: 'locatorSortPracticeReviews',
          ...GA_OBJECT,
        },
      };
    },
  };

  static searchResults = {
    geoLocationAllowed: {
      action: 'Click_Location_Button',
      category: 'Locator_Search_Bar',
    },

    loadedPractices: (practice, index, currentPage, limit, currentFilters) => {
      const { identifier } = practice;
      const practiceId = identifier;
      let listingPosition = -1;

      if (currentPage > 1) {
        listingPosition = (currentPage - 1) * limit + (index + 1);
      } else {
        listingPosition = index + 1;
      }

      return {
        GA: {
          ...this.practice.ecpShown,
          label: `practice-id:${practiceId};page:${currentPage};listing-pos:${listingPosition};${currentFilters}`,
        },
        GTM: {
          event: 'ECP_Listing_Shown',
          locatorPracticeId: practiceId,
          locatorPage: currentPage,
          locatorListingPos: listingPosition,
          locatorFilters: currentFilters,
        },
      };
    },

    resultsSearch: {
      action: 'Click_Find_Button',
      category: 'Ecp_Results_List_Header',
      nonInteraction: false,
    },

    toggleBooking: (isActive, queryStrings) => {
      let gaObject = {
        label: `${queryStrings.replace(/&/g, ';').replace(/%20/g, ' ')}`,
      };

      if (isActive) {
        gaObject = {
          ...gaObject,
          ...this.filters.toggleBookingOn,
        };
      } else {
        gaObject = {
          ...gaObject,
          ...this.filters.toggleBookingOff,
        };
      }

      return {
        GA: gaObject,
        GTM: {
          ...gaObject,
          event: isActive
            ? 'locatorToggleBookingOn'
            : 'locatorToggleBookingOff',
        },
      };
    },
  };

  static search = {
    allowLocation: {
      GA: this.searchResults.geoLocationAllowed,
      GTM: { event: 'locatorLocationAllowed' },
    },

    declineLocation: {
      GTM: {
        event: 'locatorLocationDecline',
        locatorErrorMessage: 'User declined using navigation',
        fatal: 'false',
      },
      GA_Exception: {
        description: 'User declined using navigation',
        fatal: false,
      },
    },

    geocodeError: {
      GTM: {
        event: 'Unable to retrieve geocode address.',
        fatal: 'false',
      },
      GA_Exception: {
        description: 'Unable to retrieve geocode address.',
        fatal: false,
      },
    },
  };
}

/**
 * Represents expert badge types
 */
export class Badges {
  /**
   * Indicates a blue badge type.
   */
  static blue = 'BLUE';

  /**
   * Indicates an eexpoert badge type.
   */
  static eExpert = 'E_EXPERT';

  /**
   * Indicates an onopro badge type.
   */
  static oNPro = 'O_N_PRO';

  /**
   * Indicates a tcert badge type.
   */
  static tCert = 'T_CERT';
}

/**
 * Represents Booking form options, used mainly for Europe.
 */
export class BookingFormOptions {
  /**
   * Indicates an ocuco embed form
   */
  static ocucoEmbed = 'ocuco-embed';

  /**
   * Indicates an ecp portal embed form
   */
  static ecpPortalEmbed = 'ecp-portal-embed';

  /**
   * Indicates a Salesforce form.
   */
  static salesforceForm = 'sf-form';
}

/**
 * Represents strongly typed breakpoint values.
 */
export class Breakpoints {
  /**
   * Represents desktop.
   */
  static desktop = 'desktop';

  /**
   * Represents mobile.
   */
  static mobile = 'mobile';

  /**
   * Represents tablet.
   */
  static tablet = 'tablet';
}

/**
 * Represents strongly typed filter names.
 */
export class Filters {
  /**
   * Indicates the Badges filter.
   */
  static badges = 'Badges';

  /**
   * Indicates the Hours filter.
   */
  static hours = 'Hours';

  /**
   * Indicates the online booking filter.
   */
  static onlineBooking = 'Online Booking';

  /**
   * Indicates the open weekend filter.
   */
  static openWeekend = 'Open_Weekend';

  /**
   * Indicates the Star Rating filter.
   */
  static starRating = 'Star Rating';
}

/**
 * Represents strongly typed filter groups.
 */
export class FilterGroups {
  /**
   * Indicates the Badges group.
   */
  static badges = 'Badges';

  /**
   * Indicates the Distance group.
   */
  static distance = 'Distance';

  /**
   * Indicates the Filters group.
   */
  static filters = 'Filters';

  /**
   * Indicates the Offers group.
   */
  static offers = 'Offers';

  /**
   * Indicates the Brands group
   */
  static brands = 'Brands';

  /**
   * Indicates the Online Booking filter.
   */
  static onlineBooking = 'Online Booking';

  /**
   * Indicates the Segments group.
   */
  static segments = 'Segments';
}

/**
 * Represents strongly typed pub/sub events.
 */
export class PubSubEvents {
  /**
   * Indicates an event when a practice is selected.
   */
  static practiceSelected = 'practice-selected';

  /**
   * Indicates an event when book now is clicked from practice and map
   */
  static bookingPracticeSelected = 'booking-practice-selected';

  /**
   * Indicates an event when its time to show the search filters modal.
   */
  static showSearchFiltersModal = 'show-search-filters-modal';

  /**
   * Indicates an event for toggling the visibility of the online booking.
   */
  static showOnlineBookingVisibility = 'show-online-booking-visibility';

  /**
   * Indicates an event for toggling the visibility of the user location marker on the map
   */
  static showUserLocation = 'show-user-location';

  /**
   * Indicates an event for appending chain search in the url
   */
  static appendChainSearch = 'append-chain-search';

  /**
   * Indicates an event for toggling the visibility of the results drawer.
   */
  static showResultsDrawer = 'show-results-drawer';

  /**
   * Indicates an event when a practice is updated (favorites).
   */
  static practiceUpdated = 'practice-updated';

  /**
   * Indicates an event for adding/removing a practice from user favorites.
   */
  static toggleFavorite = 'toggle-favorite';
}

export class Routes {
  static details = 'practice';

  static home = '';

  static results = 'result';

  static tabliteBooking = 'tabliteBooking';
}

export class SessionStorageKeys {
  static selectedFilters = 'selected-filters';

  /**
   * Indicates the tenant data key.
   */
  static tenantData = 'tenant_data';

  static allowLocationAccess = 'allow_location_access';

  static userCurrentLocation = 'user_current_location';

  static isSubsequentSearch = 'is_subsequent_search';

  static isGeo = 'is_geo';
}

/**
 * Represents strongly typed search filter groups.
 */
export class SearchFilterGroups {
  /**
   * Indicates the Badges group.
   */
  static badges = 'Badges';

  /**
   * Indicates the Distance group.
   */
  static distance = 'Distance';

  /**
   * Indicates the Filters group.
   */
  static filters = 'Filters';

  /**
   * Indicates the Offers group.
   */
  static offers = 'Offers';

  /**
   * Indicates the Brands group
   */
  static brands = 'Brands';

  /**
   * Indicates the Online Booking filter.
   */
  static onlineBooking = 'Online Booking';

  /**
   * Indicates the Segments group.
   */
  static segments = 'Segments';

  /**
   * Indicates the Hours group.
   */
  static hours = 'Hours';

  /**
   * Indicates the Products group.
   */
  static products = 'Products';

  /**
   * Indicates the Services group.
   */
  static services = 'Services';

  /**
   * Indicates the Frames group.
   */
  static frames = 'Frames';

  /**
   * Indicates the Language group.
   */
  static language = 'Language';

  /**
   * Indicates the Store Type group.
   */
  static storeType = 'StoreType';

  /**
   * Indicates the Service Type group.
   */
  static serviceType = 'ServiceType';

  /**
   * Indicates the Device group.
   * Used by aav_china and aav_china2
   */
  static device = 'Device';
}

// Lock object to prevent modification (true static).
Object.freeze(BookingFormOptions);
Object.freeze(Breakpoints);
Object.freeze(SessionStorageKeys);

export class ReviewSources {
  /**
   * Indicates reviews from google.
   */
  static google = 'google';

  /**
   * Indicates reviews from facebook.
   */
  static facebook = 'facebook';

  /**
   * Indicates reviews from yelp.
   */
  static yelp = 'yelp';
}

export class AdobeOnClickEventId {
  static bookNow = 'OEE_BookNow';

  static callNow = 'OEE_CallNow';

  static getDirection = 'OEE_StoreGetDirections';

  static storeDetails = 'OEE_StoreDetails';
}

/**
 * Represents Brand Names typed in search suggestions.
 */
export class BrandNameSearches {
  /**
   * Indicates LensCrafters brand name
   */
  static lenscrafters = 'LensCrafters';

  /**
   * Indicates Target Optical brand name
   */
  static targetOptical = 'Target Optical';

  /**
   * Indicates Pearle Vision brand name
   */
  static pearleVision = 'Pearle Vision';
}
